import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "../../infrastructure/api/client";
import {
  ApiError,
  type GroupResponseDto,
  type GroupMemberResponseDto,
  type GroupRole,
  type MemberType,
} from "@tabipla/shared";
import { groupKeys } from "./queryKeys";
import {
  invalidateGroupQueries,
  invalidateGroupDetail,
  invalidateGroupMembers,
} from "./invalidation";

// Fetch all groups for the current user
export function useGroups() {
  return useQuery({
    queryKey: groupKeys.all,
    queryFn: async () => {
      const response = await apiClient.get<GroupResponseDto[]>("/api/groups");
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to fetch groups");
      }
      return response.data ?? [];
    },
  });
}

// Fetch single group
export function useGroup(groupId: string | undefined) {
  return useQuery({
    queryKey: groupKeys.detail(groupId ?? ""),
    queryFn: async () => {
      if (!groupId) throw new Error("Group ID is required");
      const response = await apiClient.get<GroupResponseDto>(`/api/groups/${groupId}`);
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to fetch group");
      }
      return response.data;
    },
    enabled: !!groupId,
  });
}

// Fetch group members
export function useGroupMembers(groupId: string | undefined) {
  return useQuery({
    queryKey: groupKeys.members(groupId ?? ""),
    queryFn: async () => {
      if (!groupId) throw new Error("Group ID is required");
      const response = await apiClient.get<GroupMemberResponseDto[]>(
        `/api/groups/${groupId}/members`
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to fetch members");
      }
      return response.data ?? [];
    },
    enabled: !!groupId,
  });
}

// Create group
interface CreateGroupInput {
  name: string;
  description?: string | null;
}

export function useCreateGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateGroupInput) => {
      const response = await apiClient.post<GroupResponseDto>("/api/groups", input);
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to create group");
      }
      return response.data;
    },
    onSuccess: () => {
      invalidateGroupQueries(queryClient);
    },
  });
}

// Update group
interface UpdateGroupInput {
  name?: string;
  description?: string | null;
}

export function useUpdateGroup(groupId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: UpdateGroupInput) => {
      const response = await apiClient.put<GroupResponseDto>(`/api/groups/${groupId}`, input);
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to update group");
      }
      return response.data;
    },
    onSuccess: () => {
      invalidateGroupQueries(queryClient);
      invalidateGroupDetail(queryClient, groupId);
    },
  });
}

// Delete group
export function useDeleteGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (groupId: string) => {
      const response = await apiClient.delete<null>(`/api/groups/${groupId}`);
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to delete group");
      }
    },
    onSuccess: () => {
      invalidateGroupQueries(queryClient);
    },
  });
}

// Invite member
interface InviteMemberInput {
  email: string;
  role: Exclude<GroupRole, "owner">;
}

export function useInviteMember(groupId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: InviteMemberInput) => {
      const response = await apiClient.post<GroupMemberResponseDto>(
        `/api/groups/${groupId}/members`,
        input
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to invite member");
      }
      return response.data;
    },
    onSuccess: () => {
      invalidateGroupMembers(queryClient, groupId);
      invalidateGroupDetail(queryClient, groupId);
      invalidateGroupQueries(queryClient);
    },
  });
}

// Update member role
export function useUpdateMemberRole(groupId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      memberId,
      memberType,
      role,
    }: {
      memberId: string;
      memberType: MemberType;
      role: Exclude<GroupRole, "owner">;
    }) => {
      const response = await apiClient.put<GroupMemberResponseDto>(
        `/api/groups/${groupId}/members/${memberId}?memberType=${memberType}`,
        { role }
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to update member role");
      }
      return response.data;
    },
    onSuccess: () => {
      invalidateGroupMembers(queryClient, groupId);
    },
  });
}

// Remove member
export function useRemoveMember(groupId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ memberId, memberType }: { memberId: string; memberType: MemberType }) => {
      const response = await apiClient.delete<null>(
        `/api/groups/${groupId}/members/${memberId}?memberType=${memberType}`
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to remove member");
      }
    },
    onSuccess: () => {
      invalidateGroupMembers(queryClient, groupId);
      invalidateGroupDetail(queryClient, groupId);
      invalidateGroupQueries(queryClient);
    },
  });
}

// Add guest (proxy registration)
interface AddGuestInput {
  name: string;
  role?: Exclude<GroupRole, "owner">;
}

export function useAddGuest(groupId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: AddGuestInput) => {
      const response = await apiClient.post<GroupMemberResponseDto>(
        `/api/groups/${groupId}/guests`,
        input
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to add guest");
      }
      return response.data;
    },
    onSuccess: () => {
      invalidateGroupMembers(queryClient, groupId);
      invalidateGroupDetail(queryClient, groupId);
      invalidateGroupQueries(queryClient);
    },
  });
}
